import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from 'src/components';
import * as S from './styles';

const msgs = defineMessages({
  progress: { id: 'blob_session_progress', defaultMessage: '{current} of {total}' },
  exit: { id: 'learning_session_tooltip_exit' },
});

export const getCurrentItem = (percentComplete: number, itemCount: number) =>
  // round undoes float noise when percentComplete is ((index - 1) / total) * 100
  Math.min(itemCount, Math.round((percentComplete / 100) * itemCount) + 1);

const getProgress = (percentComplete: number, itemCount: number) => {
  const total = Math.max(itemCount, 1);
  return { current: getCurrentItem(percentComplete, total), total };
};

type ProgressSegmentsProps = {
  percentComplete: number;
  itemCount: number;
  overlay?: boolean;
};

export const ProgressSegments: FC<ProgressSegmentsProps> = ({
  percentComplete,
  itemCount,
  overlay = false,
}) => {
  const intl = useIntl();
  const { current, total } = getProgress(percentComplete, itemCount);

  return (
    <S.Segments
      role="progressbar"
      aria-label={intl.formatMessage(msgs.progress, { current, total })}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current}
      $overlay={overlay}
      data-testid="blob-session-progress"
    >
      {Array.from({ length: total }, (_, index) => (
        <S.Segment key={index} $filled={index < current} $overlay={overlay} />
      ))}
    </S.Segments>
  );
};

type SessionProgressProps = {
  percentComplete: number;
  itemCount: number;
  onExit: () => void;
};

export const SessionProgress: FC<SessionProgressProps> = ({
  percentComplete,
  itemCount,
  onExit,
}) => {
  const intl = useIntl();

  return (
    <S.Container>
      <S.ProgressTrack>
        <ProgressSegments percentComplete={percentComplete} itemCount={itemCount} />
      </S.ProgressTrack>
      <S.ExitButton
        type="button"
        onClick={onExit}
        aria-label={intl.formatMessage(msgs.exit)}
        data-testid="blob-session-exit"
      >
        <Icon name="close" />
      </S.ExitButton>
    </S.Container>
  );
};
